import { convertPlatformShortcode, Icon } from '@buggy/shared'
import { skipToken } from '@reduxjs/toolkit/query/react'
import { Button, Divider } from 'antd'
import isNil from 'lodash-es/isNil'
import { FunctionComponent, ReactNode } from 'react'
import styled from 'styled-components'
import { useFetchUserStatusQuery } from '@/api/user-api'
import { useFetchKolSubscriptionStateQuery } from '@/api/workspace-api/kol-api'
import { useSubscribeCrawlerStatusMutation } from '@/api/workspaces-api'
import DataInsufficient from '@/components/kol/common/data-insufficient'
import DurationDropdown from '@/components/kol/common/duration-dropdown'
import UnlockButton from '@/components/kol/common/unlock-button'
import { createTourStepData } from '@/components/tour/utils'
import { KolDetailDuration } from '@/hooks/use-authorization/routes'
import { usePermissions } from '@/hooks/use-permissions'
import { useIntl } from '@/i18n/hooks/use-intl'
import { KolSchemaModel } from '@/types/schema/kol-schema'
import { Platform } from '@/utils/convert-platform'

type UseSubscribeKolData = (
  kolId?: string,
  kolDcIdHashed?: string,
  platformShortCode?: string,
) => {
  isSubscribed: boolean
  subscribe: VoidFunction
}

const useSubscribeKolData: UseSubscribeKolData = (
  kolId,
  kolDcIdHashed,
  platformShortCode,
) => {
  const { withKolPermission } = usePermissions()

  const [subscribe] = useSubscribeCrawlerStatusMutation()
  const { data: userStatus } = useFetchUserStatusQuery(
    withKolPermission ? skipToken : undefined,
  )
  const { data: subscriptionStateResponse } = useFetchKolSubscriptionStateQuery(
    !withKolPermission && userStatus?.currentWorkspaceId && kolId
      ? {
          workspaceId: userStatus.currentWorkspaceId,
          radar_uuid: kolId,
          dc_id_hashed: kolDcIdHashed,
        }
      : skipToken,
  )

  const handleSubscribe: VoidFunction = () => {
    const platform =
      convertPlatformShortcode(platformShortCode)?.title.toLocaleLowerCase()

    if (!kolId || !userStatus?.currentWorkspaceId || !platform) {
      return
    }

    subscribe({
      kolUUID: kolId,
      platform,
    })
  }
  return {
    isSubscribed: !!subscriptionStateResponse?.isSubscribe,
    subscribe: handleSubscribe,
  }
}

interface KolDetailOverviewHeaderProps {
  kolInfo?: KolSchemaModel
  platform?: Platform
  isFetchingKolInfo: boolean
  isKolInfoLocked: boolean
  hiddenActions?: boolean
  duration: KolDetailDuration
  enable3MonthDuration: boolean
}

const KolDetailOverviewHeader: FunctionComponent<
  KolDetailOverviewHeaderProps
> = ({
  kolInfo,
  isKolInfoLocked,
  hiddenActions,
  platform,
  duration,
  enable3MonthDuration,
  isFetchingKolInfo,
}) => {
  const { formatMessage, formatDate } = useIntl()
  const { withKolPermission } = usePermissions()
  const { isSubscribed, subscribe } = useSubscribeKolData(
    kolInfo?.id,
    kolInfo?.dcIdHashed,
    platform?.shortcode,
  )

  const kpi = kolInfo?.kpi.find((kpi) => kpi.platform === platform?.shortcode)
  const dataUpdateTime = `${formatMessage({
    id: 'kol:general_last_update',
  })}：${
    kpi && !isNil(kpi?.latestDataUpdatedAt)
      ? formatDate(kpi.latestDataUpdatedAt)
      : formatMessage({ id: 'general:empty' })
  }`

  return (
    <Wrapper
      {...(isKolInfoLocked || !kolInfo
        ? {}
        : createTourStepData('social-performance', 2))}
    >
      <div>{dataUpdateTime}</div>
      {((): ReactNode => {
        if (hiddenActions) {
          return null
        }

        if (!isFetchingKolInfo && kolInfo?.isExpired) {
          return (
            <>
              <Divider type='vertical' />
              <DataInsufficient />
            </>
          )
        }

        if (!isKolInfoLocked && kolInfo?.withNewDataToUnlock) {
          return (
            <>
              <Divider type='vertical' />
              <UnlockButton
                buttonFontWeight={400}
                buttonI18nId='kol:unlock_latest_data'
                kolInfo={kolInfo}
                size='small'
              />
            </>
          )
        }

        if (
          !withKolPermission &&
          kpi?.isCrawlerRunning &&
          kpi?.isCrawlerTriggered
        ) {
          return (
            <>
              <Divider type='vertical' />
              <DataStatusText>
                {formatMessage({ id: 'kol:detail_updating' })}
              </DataStatusText>
              {isSubscribed ? (
                <SubscribedFakeButton
                  icon={<Icon fontSize={16} name='check' />}
                  size='small'
                >
                  {formatMessage({ id: 'kol:subscribed' })}
                </SubscribedFakeButton>
              ) : (
                <SubscribeButton
                  icon={<Icon fontSize={16} name='bell' />}
                  size='small'
                  type='primary'
                  onClick={subscribe}
                >
                  {formatMessage({ id: 'kol:reminder_after_updated' })}
                </SubscribeButton>
              )}
            </>
          )
        }
      })()}
      <Divider type='vertical' />
      <DurationDropdown
        disabledSixMonths={isKolInfoLocked}
        enable3MonthDuration={enable3MonthDuration}
        kolInfo={kolInfo}
        value={duration}
      />
    </Wrapper>
  )
}

export default KolDetailOverviewHeader

const SubscribeButton = styled(Button)`
  display: flex;
  align-items: center;
  gap: 4px;
  margin-left: 10px;
  .ant-btn-icon {
    margin-inline-end: 0 !important;
  }
`

const SubscribedFakeButton = styled(SubscribeButton)`
  color: ${({ theme }): string => theme.colors.gray7};
  gap: 4px;
  border: solid 1px ${({ theme }): string => theme.colors.gray5};
  margin-left: 10px;
  pointer-events: none;
`

const DataStatusText = styled.div`
  color: ${({ theme }): string => theme.colors.gray7};
  font-size: 13px;
  font-weight: 600;
  line-height: 140%;
`

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  height: 100%;
  color: ${({ theme }): string => theme.colors.gray7};
`
